/**
 * Leermoment-scène (§5.3): De Gids vat het leermoment samen. Na een EINDBAAS
 * volgt één reflectievraag (3 opties, gelogd in het Reisdagboek, puur flavor).
 */
import { useState } from 'react';
import type { Origin } from '../data/types';
import { CHAKRAS } from '../data/chakras';
import { HEADERS } from '../data/headers';
import { REFLECTIES } from '../data/vn';
import { baasLeermoment, reflectieVraag, reflectieAntwoord, reflectieLogregel } from '../data/content';
import { useProgressStore } from '../store/progressStore';
import { GidsTekst } from './GidsTekst';
import { ChakraAchtergrond } from './art/ChakraAchtergrond';
import { t, type Taal } from '../i18n';

interface Props {
  chakra: Origin;
  baasIndex: number;
  taal: Taal;
  onKlaar: () => void;
}

export function LeermomentScene({ chakra, baasIndex, taal, onKlaar }: Props) {
  const header = HEADERS[chakra];
  const baas = CHAKRAS[chakra].bazen[baasIndex];
  const logReflectie = useProgressStore((s) => s.logReflectie);
  const [leermomentKlaar, setLeermomentKlaar] = useState(false);
  const [gekozenIdx, setGekozenIdx] = useState<number | null>(null);

  const reflectie = baas.eindbaas ? REFLECTIES[chakra] : null;

  return (
    <ChakraAchtergrond chakra={chakra}>
    <div className="flex h-full w-full flex-col items-center justify-center gap-6 p-6 text-slate-100">
      <div className="ca-blob h-20 w-20" style={{ background: `radial-gradient(circle at 45% 40%, #f5edd6, ${header.accent} 60%, transparent 78%)`, filter: 'blur(2px)' }} />

      <GidsTekst
        tekst={baasLeermoment(baas, taal)}
        kleur={header.accent}
        onKlaar={() => setLeermomentKlaar(true)}
        klaarLabel={reflectie ? '…' : t('vn.verder', taal)}
      />

      {/* sub-baas: direct verder */}
      {leermomentKlaar && !reflectie && (
        <button type="button" onClick={onKlaar} className="rounded-lg px-6 py-2 font-semibold text-black transition hover:brightness-110" style={{ background: header.accent }}>
          {t('vn.verder', taal)} →
        </button>
      )}

      {/* eindbaas: reflectievraag */}
      {leermomentKlaar && reflectie && gekozenIdx === null && (
        <div className="flex flex-col items-center gap-3">
          <div className="text-sm uppercase tracking-widest text-slate-500">{t('vn.reflectie', taal)}</div>
          <p className="max-w-xl text-center text-lg" style={{ fontFamily: 'EB Garamond, serif', color: '#ece6da' }}>{reflectieVraag(chakra, reflectie.vraag, taal)}</p>
          <div className="flex flex-col gap-2">
            {reflectie.opties.map((o, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { setGekozenIdx(i); logReflectie(chakra, baas.naam, o.logregel ?? o.antwoord); }}
                className="rounded-xl border px-5 py-2 text-left text-sm transition hover:-translate-y-0.5"
                style={{ borderColor: header.accent, background: `linear-gradient(160deg, #14121c, ${header.kleur}22)`, color: '#ece6da' }}
              >
                {reflectieAntwoord(chakra, i, o.antwoord, taal)}
              </button>
            ))}
          </div>
        </div>
      )}

      {gekozenIdx !== null && reflectie && (
        <div className="flex flex-col items-center gap-4">
          <p className="max-w-xl text-center text-base italic text-slate-300" style={{ fontFamily: 'EB Garamond, serif' }}>
            {reflectieLogregel(chakra, gekozenIdx, reflectie.opties[gekozenIdx].logregel ?? reflectie.opties[gekozenIdx].antwoord, taal)}
          </p>
          <button type="button" onClick={onKlaar} className="rounded-lg px-6 py-2 font-semibold text-black transition hover:brightness-110" style={{ background: header.accent }}>
            {t('vn.verder', taal)} →
          </button>
        </div>
      )}
    </div>
    </ChakraAchtergrond>
  );
}
